import { useTranslation } from 'react-i18next'
import { ArrowLeft, Brain, Eye, Lock, ExternalLink, ShieldCheck } from 'lucide-react'
import { Browser } from '@capacitor/browser'
import { Capacitor } from '@capacitor/core'

interface AIConsentStepProps {
  onAgree: () => void
  onBack: () => void
}

export default function AIConsentStep({ onAgree, onBack }: AIConsentStepProps) {
  const { t } = useTranslation('onboarding')

  const openPrivacyPolicy = async () => {
    const url = 'https://tend.bot/privacy'
    if (Capacitor.isNativePlatform()) {
      await Browser.open({ url })
    } else {
      window.open(url, '_blank')
    }
  }

  return (
    <div>
      <div className="text-center mb-5">
        <div className="w-12 h-12 rounded-full bg-gold/10 border border-gold/20 flex items-center justify-center mx-auto mb-3">
          <Brain size={24} className="text-gold" />
        </div>
        <h2 className="font-display text-2xl text-ivory font-semibold mb-1">
          {t('aiConsent.heading')}
        </h2>
        <p className="text-pearl/50 text-sm">
          {t('aiConsent.subtitle')}
        </p>
      </div>

      {/* What is shared with the AI */}
      <div className="glass-card rounded-xl p-4 mb-3">
        <div className="flex items-center gap-2 mb-2.5">
          <Eye size={16} className="text-gold" />
          <span className="text-ivory text-sm font-medium">{t('aiConsent.sharedTitle')}</span>
        </div>
        <ul className="space-y-1.5">
          {['name', 'calendar', 'emails', 'notebook', 'cortex', 'history', 'audio'].map(key => (
            <li key={key} className="text-pearl/60 text-xs leading-relaxed flex gap-2">
              <span className="text-gold/50 mt-px shrink-0">•</span>
              <span>{t(`aiConsent.shared.${key}`)}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* How data is protected */}
      <div className="glass-card rounded-xl p-4 mb-4">
        <div className="flex items-center gap-2 mb-2.5">
          <Lock size={16} className="text-emerald-400" />
          <span className="text-ivory text-sm font-medium">{t('aiConsent.protectedTitle')}</span>
        </div>
        <ul className="space-y-1.5">
          {['noTraining', 'encrypted', 'temporary'].map(key => (
            <li key={key} className="text-pearl/60 text-xs leading-relaxed flex gap-2">
              <span className="text-emerald-400/50 mt-px shrink-0">•</span>
              <span>{t(`aiConsent.protected.${key}`)}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Privacy policy link */}
      <button
        onClick={openPrivacyPolicy}
        className="w-full text-center text-gold/70 hover:text-gold text-xs transition-colors flex items-center justify-center gap-1 mb-6"
      >
        {t('aiConsent.readPolicy')}
        <ExternalLink size={12} />
      </button>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-pearl/50 hover:text-pearl text-sm transition-colors"
        >
          <ArrowLeft size={16} />
          {t('nav.back')}
        </button>
        <button
          onClick={onAgree}
          className="flex items-center gap-2 bg-gold hover:bg-gold-light text-midnight font-semibold px-6 py-2.5 rounded-xl transition-colors"
        >
          <ShieldCheck size={16} />
          {t('aiConsent.agree')}
        </button>
      </div>
    </div>
  )
}
